
import { logger } from '@/utils/logger';
import { createRoot } from 'react-dom/client'
import App from './App.tsx'
import './index.css'
import { supabase, getSupabaseUrl } from './integrations/supabase/client';
import { handleError, ErrorSource } from './utils/errorHandling';
import { initSafeguards } from './disableLockdown';

// Declare these globals for TypeScript
declare global {
  interface Window {
    lockdown?: () => boolean;
    SES?: {
      lockdown?: () => boolean;
    };
  }
}

// Initialize lockdown safeguards
try {
  logger.info("Initializing security safeguards...");
  initSafeguards();
} catch (err) {
  logger.warn("Failed to initialize safeguards:", err);
}

// Add global error handling with improved tracking
const globalErrorHandler = (event: ErrorEvent) => {
  // Check if it's a lockdown error
  const isLockdownError = 
    String(event.error || '').includes('lockdown') || 
    String(event.error || '').includes('Removing unpermitted intrinsic');
  
  // Log with additional context
  logger.error("Global error caught:", event.error, {
    module: "main",
    errorStack: event.error?.stack,
    url: window.location.href,
    // Additional properties
    data: { isLockdownError }
  });
  
  // Use centralized error handler
  handleError(event.error || new Error("Unknown global error"), {
    source: ErrorSource.General,
    context: "global-error-handler",
    important: true,
    url: window.location.href
  });
  
  // Don't prevent default for lockdown errors - let the ErrorBoundary handle them
  if (!isLockdownError) {
    event.preventDefault();
  }
};

// Skip Lockdown errors in global error handler
const globalErrorFilter = (event: ErrorEvent) => {
  const errorString = String(event.error || event.message || '');
  
  // Completely suppress these known benign errors
  if (errorString.includes('lockdown') || 
      errorString.includes('Removing unpermitted intrinsic') ||
      errorString.includes('SES') ||
      errorString.includes('Unrecognized feature:')) {
    // Don't log anything to keep console clean
    event.preventDefault();
    event.stopPropagation();
    return false;
  }
  
  // Otherwise use the main handler
  return globalErrorHandler(event);
};

window.addEventListener('error', globalErrorFilter);

// Add global unhandled promise rejection handling with improved tracking
window.addEventListener('unhandledrejection', (event) => {
  // Check if it's a lockdown error
  const errorString = String(event.reason || '');
  const isLockdownError = 
    errorString.includes('lockdown') || 
    errorString.includes('Removing unpermitted intrinsic') ||
    errorString.includes('SES') ||
    errorString.includes('Unrecognized feature:') ||
    errorString.includes('Failed to load resource');
  
  // For benign errors, completely suppress them to keep console clean
  if (isLockdownError) {
    event.preventDefault();
    return;
  }
  
  logger.error("Unhandled Promise Rejection:", event.reason, {
    module: "main",
    url: window.location.href
  });
  
  // Use centralized error handler
  handleError(event.reason || new Error("Unknown promise rejection"), {
    source: ErrorSource.General,
    context: "unhandled-rejection",
    important: true,
    url: window.location.href
  });
});

// Check for special modes from URL
const urlParams = new URLSearchParams(window.location.search);
const isRecoveryMode = urlParams.get('recovery') === 'true';
const isLoginDebug = window.location.pathname.includes('/debug/login');
const isDebugMode = urlParams.get('debug') === 'true';
const isBypassMode = urlParams.get('bypass') === 'true';
const isNoLockdownMode = urlParams.get('no_lockdown') === 'true';
const isSafeMode = urlParams.get('safe_mode') === 'true';

// Handle no_lockdown mode by trying to disable lockdown.js
if (isNoLockdownMode) {
  try {
    logger.info("NO_LOCKDOWN mode enabled - attempting to disable lockdown.js");
    // Try to override lockdown function
    window.lockdown = function() {
      logger.info("Lockdown call intercepted and disabled");
      return true;
    };
    
    // Also try to neutralize any SES-related restrictions
    if (window.SES) {
      logger.info("SES detected, attempting to configure it permissively");
      try {
        window.SES.lockdown = window.lockdown;
      } catch (sesErr) {
        logger.warn("Error overriding SES lockdown:", sesErr);
      }
    }
  } catch (err) {
    logger.warn("Failed to disable lockdown:", err);
  }
}

// Add global handler for resource loading errors
const handleResourceError = (event: Event) => {
  const target = event.target as HTMLElement;
  if (target instanceof HTMLScriptElement || target instanceof HTMLLinkElement || target instanceof HTMLImageElement) {
    const url = target instanceof HTMLScriptElement ? target.src : 
               (target instanceof HTMLLinkElement ? target.href : 
               (target instanceof HTMLImageElement ? target.src : ''));
    
    // Only log non-lockdown errors
    if (url && !url.includes('lockdown') && !url.includes('SES')) {
      logger.warn(`Resource failed to load (silently continuing): ${url.substring(0, 100)}...`);
    }
    
    // Don't propagate the error further
    event.stopPropagation();
  }
};

// Override console methods to suppress feature detection warnings
const originalConsoleWarn = console.warn;
const originalConsoleError = console.error;

console.warn = function(...args: unknown[]) {
  // Check if this is a feature detection warning we want to suppress
  const message = args[0]?.toString() || '';
  if (message.includes('Unrecognized feature:') || 
      message.includes('Failed to load resource:') ||
      message.includes('The resource') && message.includes('was preloaded using link preload but not used')) {
    // Suppress these warnings
    return;
  }
  
  // Pass through to original console method
  originalConsoleWarn.apply(console, args);
};

console.error = function(...args: unknown[]) {
  // Check if this is a feature detection error we want to suppress
  const message = args[0]?.toString() || '';
  if (message.includes('Unrecognized feature:') || 
      message.includes('Failed to load resource:') ||
      message.includes('Chrome is moving towards a new experience')) {
    // Suppress these errors
    return;
  }
  
  // Pass through to original console method
  originalConsoleError.apply(console, args);
};

// Add event listener for resource errors
document.addEventListener('error', handleResourceError, true);

// Handle external script errors in recovery mode
if (isRecoveryMode) {
  // Find all script tags loading external resources
  const externalScripts = document.querySelectorAll('script[src^="https://"]');
  
  // If in recovery mode, add error handling to external scripts
  externalScripts.forEach(script => {
    const scriptElement = script as HTMLScriptElement;
    const originalSrc = scriptElement.src;
    
    if (originalSrc && (originalSrc.includes('cdn.gpteng.co') || originalSrc.includes('lovable.app'))) {
      logger.info("RECOVERY: Detected external script, adding error handling", originalSrc);
      
      // Create a new script element with error handling
      try {
        // Remove the original script to prevent it from loading
        script.parentNode?.removeChild(script);
        
        // Create a new script element with error handling
        const newScript = document.createElement('script');
        newScript.src = originalSrc;
        newScript.type = 'module';
        
        // Add error handling
        newScript.onerror = (error) => {
          logger.warn("RECOVERY: External script failed to load, continuing without it:", error);
          // Don't break the app, just continue without the script
        };
        
        // Add a timeout to prevent hanging
        const timeout = setTimeout(() => {
          logger.warn("RECOVERY: External script load timed out, continuing without it");
          // Don't break the app, just continue without the script
          newScript.remove();
        }, 5000);
        
        newScript.onload = () => {
          clearTimeout(timeout);
          logger.info("RECOVERY: External script loaded successfully");
        };
        
        // Add the script to the document
        document.head.appendChild(newScript);
      } catch (error) {
        logger.error("RECOVERY: Failed to handle external script:", error);
      }
    }
  });
  
  // Also handle CSS stylesheets
  const externalStylesheets = document.querySelectorAll('link[rel="stylesheet"][href^="https://"]');
  externalStylesheets.forEach(link => {
    const linkElement = link as HTMLLinkElement;
    const originalHref = linkElement.href;
    
    if (originalHref && originalHref.includes('lovable.app')) {
      logger.info("RECOVERY: Detected external stylesheet, adding error handling", originalHref);
      
      // Add error handling
      linkElement.onerror = () => {
        logger.warn("RECOVERY: External stylesheet failed to load, continuing without it:", originalHref);
        // Don't break the app, just continue without the stylesheet
      };
    }
  });
}

// Debugging info
logger.info("=====================================================");
logger.info("DEBUG: Application starting with Supabase Edge Function debugging enabled");
logger.info("Supabase URL:", getSupabaseUrl());
logger.info("Application build timestamp:", import.meta.env.VITE_BUILD_TIMESTAMP || 'Not available');
logger.info("Recovery mode:", isRecoveryMode ? "ENABLED" : "DISABLED");
logger.info("Login debug mode:", isLoginDebug ? "ENABLED" : "DISABLED");

// Handle recovery and debug modes
if (isRecoveryMode || isLoginDebug) {
  try {
    if (isRecoveryMode) {
      logger.info("DEBUG: Running in recovery mode - clearing storage");
      localStorage.clear();
      sessionStorage.clear();
      logger.info("DEBUG: Storage cleared in recovery mode");
    }
    
    // Special handling for login debug page
    if (isLoginDebug) {
      logger.info("DEBUG: Running in login debug mode - extended logging enabled");
      // Override console methods for more verbose logging in debug mode
      const originalConsoleLog = console.log;
      console.log = function(...args: unknown[]) {
        if (args[0]?.toString()?.includes('AUTH:')) {
          args[0] = `[DEBUG ENHANCED] ${args[0]}`;
        }
        originalConsoleLog.apply(console, args);
      };
    }
  } catch (error) {
    logger.error("DEBUG: Failed to handle special mode:", error);
  }
}

// Check if Edge Functions are accessible by doing a simple test call
(async function checkEdgeFunctions() {
  try {
    logger.info("DEBUG: Running Edge Function connectivity test...");
    // Get the current auth status to see if we can call Edge Functions
    const { data: { session } } = await supabase.auth.getSession();
    logger.info("DEBUG: Auth session:", session ? "ACTIVE" : "NONE", 
      session?.user?.id ? `(User ID: ${session.user.id})` : "(No user ID)");

    if (!session) {
      logger.warn("DEBUG: No active session found - Edge Functions requiring auth will fail!");
    } else {
      logger.info("DEBUG: User is authenticated, Edge Functions should work!");
    }
  } catch (error) {
    logger.error("DEBUG: Error checking Supabase Edge Functions:", error);
  }
})();

// Mount the application with error boundary and fallback
const container = document.getElementById("root");

// URL parameters are already defined above, don't redefine them

// Add a simple loading indicator immediately to show something while initializing
if (container) {
  container.innerHTML = `
    <div style="display:flex; justify-content:center; align-items:center; height:100vh; flex-direction:column;">
      <div style="border:4px solid #f3f3f3; border-top:4px solid #3498db; border-radius:50%; width:40px; height:40px; animation:spin 1s linear infinite;"></div>
      <p style="margin-top:20px;">Initializing application...</p>
      <style>@keyframes spin { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }</style>
    </div>
  `;

  // Retry mount with incremental backoff if needed
  const mountApp = (retries = 3, delay = 500) => {
    try {
      logger.info(`DEBUG: Attempting to mount app (retry ${4-retries}/3)`);
      const root = createRoot(container);
    
      if (isDebugMode && !isBypassMode) {
        // In debug mode, show a debug interface first
        logger.info("DEBUG: Rendering debug interface first to isolate rendering issues");
        
        root.render(
          <div style={{padding: '20px', fontFamily: 'monospace'}}>
            <h2>Debugging Render</h2>
            <p>Testing basic React rendering...</p>
            <p>If you see this, basic React rendering is working.</p>
            <hr />
            <div id="debug-info">
              <p>Browser: {navigator.userAgent}</p>
              <p>URL: {window.location.href}</p>
              <p>Time: {new Date().toISOString()}</p>
            </div>
            <button 
              style={{padding: '8px 16px', background: '#0070f3', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer'}}
              onClick={() => {
                logger.info("Attempting to render full app now...");
                try {
                  root.render(<App />);
                  logger.info("DEBUG: React app successfully rendered");
                } catch (err) {
                  logger.error("DETAILED ERROR:", err);
                  document.getElementById('debug-error')!.textContent = JSON.stringify(err, null, 2);
                }
              }}
            >
              Load Full App
            </button>
            <div id="debug-error" style={{whiteSpace: 'pre-wrap', marginTop: '20px', padding: '10px', background: '#f5f5f5', border: '1px solid #ddd', borderRadius: '4px', maxHeight: '300px', overflow: 'auto'}}></div>
          </div>
        );
        
        logger.info("DEBUG: Basic React test component rendered");
      } else {
        // In normal mode, render the app directly
        logger.info("DEBUG: Rendering full app directly");
        root.render(<App />);
        logger.info("DEBUG: React app rendered");
        
        // Add a timeout to detect if nothing renders within 5 seconds
        setTimeout(() => {
          if (root && container && (!container.children || container.children.length === 0 || container.innerHTML.trim() === '')) {
            logger.error("ERROR: No content rendered after 5 seconds, possible blank screen issue");
            // Log additional diagnostic information
            logger.info("Browser:", navigator.userAgent);
            logger.info("URL:", window.location.href);
            logger.info("Time:", new Date().toISOString());
            
            // Suggest recovery options
            logger.info("RECOVERY: Try visiting /?debug=true or /test.html for debugging");
            
            // Show fallback UI directly in the container
            container.innerHTML = `
              <div style="padding: 20px; text-align: center; font-family: sans-serif;">
                <h2>Application Loading Issue Detected</h2>
                <p>The application is taking longer than expected to load.</p>
                <p>Please try one of the following options:</p>
                <div style="margin-top: 20px;">
                  <a href="/?debug=true" style="display: inline-block; padding: 8px 16px; margin-right: 10px; background: #0070f3; color: white; text-decoration: none; border-radius: 4px;">
                    Debug Mode
                  </a>
                  <a href="/?recovery=true" style="display: inline-block; padding: 8px 16px; background: #f44336; color: white; text-decoration: none; border-radius: 4px;">
                    Recovery Mode
                  </a>
                </div>
                <p style="margin-top: 20px; font-size: 0.8rem;">Technical details: Blank screen timeout detected</p>
              </div>
            `;
          }
        }, 5000);
      }
    } catch (error) {
      logger.error("CRITICAL: Failed to render React application:", error);
      // Fallback rendering to show something if React fails to load
      container.innerHTML = `
        <div style="padding: 20px; text-align: center; font-family: sans-serif;">
          <h2>Application Error</h2>
          <p>There was a problem loading the application. Please try refreshing the page.</p>
          <p>If the problem persists, try clearing your browser cache or using the links below:</p>
          <div style="margin-top: 20px;">
            <button onclick="window.location.href='/?debug=true'" style="padding: 8px 16px; margin-right: 10px; background: #0070f3; color: white; border: none; border-radius: 4px; cursor: pointer;">
              Debug Mode
            </button>
            <button onclick="window.location.href='/test.html'" style="padding: 8px 16px; margin-right: 10px; background: #34c759; color: white; border: none; border-radius: 4px; cursor: pointer;">
              Test Page
            </button>
            <button onclick="localStorage.clear(); sessionStorage.clear(); window.location.href='/?recovery=true'" style="padding: 8px 16px; background: #f44336; color: white; border: none; border-radius: 4px; cursor: pointer;">
              Clear Data & Recover
            </button>
          </div>
          <div id="error-details" style="margin-top: 20px; text-align: left; background: #f5f5f5; padding: 15px; border-radius: 4px; max-height: 200px; overflow: auto;">
            <pre style="white-space: pre-wrap;">${error ? (error.stack || error.toString()) : 'Unknown error'}</pre>
          </div>
        </div>
      `;
      
      // If there are retries left, try again with delay
      if (retries > 0) {
        logger.info(`Retrying app mount in ${delay}ms, ${retries} retries left`);
        setTimeout(() => mountApp(retries - 1, delay * 2), delay);
      }
    }
  };

  // Start the mounting process
  mountApp();
} else {
  logger.error("CRITICAL: Root element not found in HTML");
}
